import { withFormik, FormikProps, Field } from 'formik';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';

import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import EmailInput from '$/components/global/emailInput';
import InputBlock from '$/components/global/inputBlock';
import DomainList from '$/components/global/domainListEditor';
import EmailSelectInput from '$/components/global/emailSelectInput';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import './UpdateClientForm.scss';
import { getBixbyComplianceFeatureFlagState, getSFDREnabled } from '$/store/release-feature-flags/selectors';
import {
  getIsCurrentUserBixbyComplianceAdminState,
  getIsCurrentUserLegalAdminState,
  getIsCurrentUserSuperAdminState,
} from '$/store/profile/selectors';

export interface UpdateClientFormValues {
  clientName: string;
  clientEmail: string;
  dataSiteContact1: string;
  dataSiteContact2: string;
  dataSiteEmail: string;
  mnpiDataSiteEmail: string;
  emailDomain: string;
  clientNotes: string;
  primaryContact1: string;
  primaryContact2: string;
  subscriptionTier: string;
  hasAnalytics: boolean;
  hasExcelAccess: boolean;
  hasCompanyTranscriptions: boolean;
  hasCompanyDealNotes: boolean;
  hasCompanyDocuments: boolean;
  hasCloAccess: boolean;
  hasCreditSightsCovenantReview: boolean;
  hasPreapprovedCompanyAccess: boolean;
  primaryComplianceAdmin?: string;
  secondaryComplianceAdmin?: string;
  hasBixbyCompliance: boolean;
  hasBixbyComplianceAutoApproval: boolean;
  hasBixbyComplianceDemoMode: boolean;
  isPrivateOnAllNames: boolean;
  canReachOutToBankOnBehalf: boolean;
  usersCannotLogin: boolean;
  hasSFDR: boolean;
  hasUsEuroGrouping: boolean;
  isSufPermissioned: boolean;
  isRatingsPermissioned: boolean;
  isCovenantCrossPermissioned: boolean;
  isOnFitchPaper: boolean;
  isInPreview: boolean;
  isCutoverToCreditSights: boolean;
  allowDocumentSharing: boolean;
  hasClientProvidedDataSites: boolean;
  hasCloPermissions: boolean;
  includeWithDataAggregation: boolean;
  hasPortfolioManagerWorkflow: boolean;
}

interface IClientFormikProps {
  subscriptions?: Bixby.Models.IIdentifiable[];
  submitButtonText?: string;
  onSubmit?: (values: UpdateClientFormValues) => Promise<void>;
  isActive?: boolean;
  initialValues?: UpdateClientFormValues;
  title?: string;
  users?: Bixby.Models.ClientContact[];
  isBixbyComplianceAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  isSFDREnabled: boolean;
  isLegalAdmin: boolean;
  isSuperAdmin: boolean;
  isCloPermissioningEnabled: boolean;
  isClientProviderDataSitesEnabled: boolean;
}

const UpdateClientFormik: React.FC<IClientFormikProps & FormikProps<UpdateClientFormValues>> = props => {
  const {
    subscriptions,
    errors,
    touched,
    handleChange,
    handleBlur,
    values,
    handleSubmit,
    isSubmitting,
    isValid,
    status,
    handleReset,
    submitButtonText,
    isActive,
    title,
    users,
    setFieldValue,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
    isSFDREnabled,
    isLegalAdmin,
    isSuperAdmin,
    isCloPermissioningEnabled,
    isClientProviderDataSitesEnabled,
  } = props;

  const domainsUpdated = (domains: string) => {
    values.emailDomain = domains;
  };

  useEffect(() => {
    if (isActive === false) {
      handleReset();
    }
  }, [handleReset, isActive]);

  const missingComplianceFields: string[] = [];
  if (values.hasBixbyCompliance) {
    if (Bixby.Utils.isNullOrEmpty(values.primaryComplianceAdmin)) {
      missingComplianceFields.push('Primary Compliance Admin');
    }

    if (Bixby.Utils.isNullOrEmpty(values.mnpiDataSiteEmail)) {
      missingComplianceFields.push('MNPI Data Site Email');
    }
  }

  const complianceDisableReason =
    missingComplianceFields.length > 0
      ? `Save is disabled. Required field(s): ${missingComplianceFields.join(', ')}.`
      : null;

  return (
    <form onSubmit={handleSubmit} className="update_client_form">
      <h3>{title}</h3>
      <div className="flexbox">
        <div>
          <InputBlock useSmall hasError={!!errors.clientName && !!touched.clientName} title="Client Name">
            <input
              onChange={handleChange}
              onBlur={handleBlur}
              id="clientName"
              name="clientName"
              value={values.clientName}
            />
            {errors.clientName && touched.clientName ? <p className="error">{errors.clientName}</p> : null}
          </InputBlock>
          <h2>Client Domains</h2>
          <InputBlock>
            <DomainList domains={values.emailDomain} updateChanges={domainsUpdated} />
          </InputBlock>
          <InputBlock hasError={!!errors.clientEmail && !!touched.clientEmail} title="Client Email" required>
            <EmailInput
              domain={Bixby.Constants.EMAIL_DOMAIN_BIXBY}
              onChange={handleChange}
              onBlur={handleBlur}
              type="text"
              id="clientEmail"
              name="clientEmail"
              value={values.clientEmail}
            />
            {errors.clientEmail && touched.clientEmail ? <p className="error">{errors.clientEmail}</p> : null}
          </InputBlock>
          <InputBlock hasError={!!errors.dataSiteEmail && !!touched.dataSiteEmail} title="Data Site Email" required>
            <EmailInput
              domain={values.emailDomain.split(',')[0]}
              onChange={handleChange}
              onBlur={handleBlur}
              type="text"
              id="dataSiteEmail"
              name="dataSiteEmail"
              value={values.dataSiteEmail}
              disabled
            />
            {errors.dataSiteEmail && touched.dataSiteEmail ? <p className="error">{errors.dataSiteEmail}</p> : null}
          </InputBlock>
          {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
            <InputBlock
              hasError={!!errors.mnpiDataSiteEmail && !!touched.mnpiDataSiteEmail}
              title="MNPI Data Site Email">
              <EmailInput
                domain={values.emailDomain.split(',')[0]}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                id="mnpiDataSiteEmail"
                name="mnpiDataSiteEmail"
                value={values.mnpiDataSiteEmail}
              />
              {values.hasBixbyCompliance && errors.mnpiDataSiteEmail ? (
                <p className="error">{errors.mnpiDataSiteEmail}</p>
              ) : null}
            </InputBlock>
          )}
          <InputBlock useSmall title="Subscription Tier" required>
            <select name="subscriptionTier" value={values.subscriptionTier} onChange={handleChange} onBlur={handleBlur}>
              <option key={-1} value="" disabled>
                Select a Subscription Tier
              </option>
              {Bixby.Utils.orderBy(subscriptions || [], i => i.name).map((t, ix) => {
                return (
                  <option
                    key={ix}
                    value={(t as Bixby.Models.IIdentifiable).id}
                    label={(t as Bixby.Models.IIdentifiable).name}
                  />
                );
              })}
            </select>
            {errors.subscriptionTier && touched.subscriptionTier ? (
              <p className="error">{errors.subscriptionTier}</p>
            ) : null}
          </InputBlock>
          <InputBlock useSmall hasError={!!errors.clientNotes && !!touched.clientNotes} title="Client Notes">
            <textarea
              onChange={handleChange}
              onBlur={handleBlur}
              id="clientNotes"
              name="clientNotes"
              value={values.clientNotes}
            />
            {errors.clientNotes && touched.clientNotes ? <p className="error">{errors.clientNotes}</p> : null}
          </InputBlock>
        </div>
        <div>
          <Field
            name="hasAnalytics"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasAnalytics"
                checked={values.hasAnalytics}
                title="Has Analytics"
                onChange={field.onChange(field.name)}>
                Has Analytics
              </BixCheckbox>
            )}
          />
          <Field
            name="hasExcelAccess"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasExcelAccess"
                checked={values.hasExcelAccess}
                title="Has Excel"
                onChange={field.onChange(field.name)}>
                Has Excel
              </BixCheckbox>
            )}
          />
          <Field
            name="hasCompanyTranscriptions"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasCompanyTranscriptions"
                checked={values.hasCompanyTranscriptions}
                title="Has Transcripts"
                onChange={field.onChange(field.name)}>
                Has Transcripts
              </BixCheckbox>
            )}
          />
          <Field
            name="hasCompanyDealNotes"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasCompanyDealNotes"
                checked={values.hasCompanyDealNotes}
                title="Has Deal Notes"
                onChange={field.onChange(field.name)}>
                Has Deal Notes
              </BixCheckbox>
            )}
          />
          <Field
            name="hasCompanyDocuments"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasCompanyDocuments"
                checked={values.hasCompanyDocuments}
                title="Has Company Documents"
                onChange={field.onChange(field.name)}>
                Has Company Documents
              </BixCheckbox>
            )}
          />
          <Field
            name="hasCloAccess"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasCloAccess"
                checked={values.hasCloAccess}
                title="Has CLO Access"
                onChange={field.onChange(field.name)}>
                Has CLO Access
              </BixCheckbox>
            )}
          />
          <Field
            name="hasCreditSightsCovenantReview"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasCreditSightsCovenantReview"
                checked={values.hasCreditSightsCovenantReview}
                title="Has CreditSights Covenant Review"
                onChange={field.onChange(field.name)}>
                Has CreditSights Covenant Review
              </BixCheckbox>
            )}
          />
          <Field
            name="hasPreapprovedCompanyAccess"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasPreapprovedCompanyAccess"
                checked={values.hasPreapprovedCompanyAccess}
                title="Has Preapproved Company Access"
                onChange={field.onChange(field.name)}>
                Has Preapproved Company Access
              </BixCheckbox>
            )}
          />
          <Field
            name="usersCannotLogin"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="usersCannotLogin"
                checked={values.usersCannotLogin}
                title="Users Cannot Login"
                onChange={field.onChange(field.name)}>
                Users Cannot Login
              </BixCheckbox>
            )}
          />
          {isBixbyComplianceEnabled && isBixbyComplianceAdmin && (
            <Field
              name="hasBixbyCompliance"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="hasBixbyCompliance"
                  checked={values.hasBixbyCompliance}
                  title="Has Compliance"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e, field.name);
                  }}>
                  Has Compliance
                </BixCheckbox>
              )}
            />
          )}
          {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
            <Field
              name="hasBixbyComplianceAutoApproval"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="hasBixbyComplianceAutoApproval"
                  checked={values.hasBixbyComplianceAutoApproval}
                  title="Has Compliance Auto Approval"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e, field.name);
                  }}>
                  Has Compliance Auto Approval
                </BixCheckbox>
              )}
            />
          )}
          {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
            <Field
              name="hasBixbyComplianceDemoMode"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="hasBixbyComplianceDemoMode"
                  checked={values.hasBixbyComplianceDemoMode}
                  title="Has Compliance Demo Mode"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e, field.name);
                  }}>
                  Has Compliance Demo Mode
                </BixCheckbox>
              )}
            />
          )}
          {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
            <Field
              name="isPrivateOnAllNames"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="isPrivateOnAllNames"
                  checked={values.isPrivateOnAllNames}
                  title="Is Private On All Names"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e, field.name);
                  }}>
                  Is Private On All Names
                </BixCheckbox>
              )}
            />
          )}
          {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
            <Field
              name="canReachOutToBankOnBehalf"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="canReachOutToBankOnBehalf"
                  checked={values.canReachOutToBankOnBehalf}
                  title="Can Reach Out To Bank On Behalf"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e, field.name);
                  }}>
                  Can Reach Out To Bank On Behalf
                </BixCheckbox>
              )}
            />
          )}
          {isSFDREnabled && (
            <Field
              name="hasSFDR"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="hasSFDR"
                  checked={values.hasSFDR}
                  title="Has SFDR"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e, field.name);
                  }}>
                  Has SFDR
                </BixCheckbox>
              )}
            />
          )}
          <Field
            name="hasUsEuroGrouping"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="hasUsEuroGrouping"
                checked={values.hasUsEuroGrouping}
                title="Has US/Euro Grouping"
                onChange={field.onChange(field.name)}>
                Has US/Euro Grouping
              </BixCheckbox>
            )}
          />
          <Field
            name="isInPreview"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="isInPreview"
                checked={values.isInPreview}
                title="Is In Preview"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e, field.name);
                }}>
                Is In Preview
              </BixCheckbox>
            )}
          />
          <Field
            name="isCutoverToCreditSights"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="isCutoverToCreditSights"
                checked={values.isCutoverToCreditSights}
                title="Is Cutover To Credit Sights"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e, field.name);
                }}>
                Is Cutover To Credit Sights
              </BixCheckbox>
            )}
          />
          <Field
            name="allowDocumentSharing"
            render={({ field }: { [key: string]: any }) => (
              <BixCheckbox
                id="allowDocumentSharing"
                checked={values.allowDocumentSharing}
                title="Allow Document Sharing"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e, field.name);
                }}>
                Allow Document Sharing
              </BixCheckbox>
            )}
          />
          {isLegalAdmin && (
            <>
              <Field
                name="isSufPermissioned"
                render={({ field }: { [key: string]: any }) => (
                  <BixCheckbox
                    id="isSufPermissioned"
                    checked={values.isSufPermissioned}
                    title="Is Suf Permissioned"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e, field.name);
                    }}>
                    Is Suf Permissioned
                  </BixCheckbox>
                )}
              />
              <Field
                name="isRatingsPermissioned"
                render={({ field }: { [key: string]: any }) => (
                  <BixCheckbox
                    id="isRatingsPermissioned"
                    checked={values.isRatingsPermissioned}
                    title="Is Ratings Permissioned"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e, field.name);
                    }}>
                    Is Ratings Permissioned
                  </BixCheckbox>
                )}
              />
              <Field
                name="isOnFitchPaper"
                render={({ field }: { [key: string]: any }) => (
                  <BixCheckbox
                    id="isOnFitchPaper"
                    checked={values.isOnFitchPaper}
                    title="Is On Fitch Paper"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e, field.name);
                    }}>
                    Is On Fitch Paper
                  </BixCheckbox>
                )}
              />
              <Field
                name="isCovenantCrossPermissioned"
                render={({ field }: { [key: string]: any }) => (
                  <BixCheckbox
                    id="isCovenantCrossPermissioned"
                    checked={values.isCovenantCrossPermissioned}
                    title="Is Covenant Cross Permissioned"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e, field.name);
                    }}>
                    Is Covenant Cross Permissioned
                  </BixCheckbox>
                )}
              />
              <Field
                name="includeWithDataAggregation"
                render={({ field }: { [key: string]: any }) => (
                  <BixCheckbox
                    id="includeWithDataAggregation"
                    checked={values.includeWithDataAggregation}
                    title="Include with Data Aggregation"
                    onChange={field.onChange(field.name)}>
                    Include with Data Aggregation
                  </BixCheckbox>
                )}
              />
            </>
          )}
          {isCloPermissioningEnabled && (
            <Field
              name="hasCloPermissions"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="hasCloPermissions"
                  checked={values.hasCloPermissions}
                  title="Has Clo Permissioning"
                  onChange={field.onChange(field.name)}>
                  Has Clo Permissioning
                </BixCheckbox>
              )}
            />
          )}
          {isClientProviderDataSitesEnabled && isSuperAdmin && (
            <Field
              name="hasClientProvidedDataSites"
              render={({ field }: { [key: string]: any }) => (
                <BixCheckbox
                  id="hasClientProvidedDataSites"
                  checked={values.hasClientProvidedDataSites}
                  title="Has Client Provided Data Sites"
                  onChange={field.onChange(field.name)}>
                  Has Client Provided Data Sites
                </BixCheckbox>
              )}
            />
          )}
          {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
            <Field
              name="hasPortfolioManagerWorkflow"
              render={() => (
                <BixCheckbox
                  id="hasPortfolioManagerWorkflow"
                  checked={values.hasPortfolioManagerWorkflow}
                  title="Has Portfolio Manager Workflow"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('hasPortfolioManagerWorkflow', e.target.checked);
                  }}>
                  Has Portfolio Manager Workflow
                </BixCheckbox>
              )}
            />
          )}
        </div>
      </div>
      <h2>Account Contacts</h2>
      <div className="flexbox account">
        <div>
          <InputBlock
            useSmall
            hasError={!!errors.primaryContact1 && !!touched.primaryContact1}
            title="Primary Admin"
            required>
            <EmailSelectInput
              datasource={
                users?.map(_ => {
                  return { id: _.email };
                }) ?? []
              }
              onChange={v => {
                const value = v.currentTarget.value;
                setFieldValue('primaryContact1', value);
                if (Bixby.Utils.isNullOrEmpty(value)) {
                  setFieldValue('primaryContact2', value);
                }
              }}
              id="primaryContact1"
              value={values.primaryContact1}
            />
            {errors.primaryContact1 && touched.primaryContact1 ? (
              <p className="error">{errors.primaryContact1}</p>
            ) : null}
          </InputBlock>
        </div>
        <div>
          <InputBlock useSmall hasError={!!errors.primaryContact2 && !!touched.primaryContact2} title="Secondary Admin">
            <EmailSelectInput
              datasource={
                users?.map(_ => {
                  return { id: _.email };
                }) ?? []
              }
              onChange={handleChange}
              id="primaryContact2"
              value={values.primaryContact2}
              disabled={Bixby.Utils.isNullOrEmpty(values.primaryContact1)}
            />
            {errors.primaryContact2 && touched.primaryContact2 ? (
              <p className="error">{errors.primaryContact2}</p>
            ) : null}
          </InputBlock>
        </div>
        <div>
          <InputBlock
            useSmall
            hasError={!!errors.dataSiteContact1 && !!touched.dataSiteContact1}
            title="Primary Data Site">
            <EmailInput
              domain={values.emailDomain.split(',')[0]}
              onChange={v => {
                const value = v.currentTarget.value;
                setFieldValue('dataSiteContact1', value);
                if (Bixby.Utils.isNullOrEmpty(value)) {
                  setFieldValue('dataSiteContact2', value);
                }
              }}
              onBlur={handleBlur}
              type="text"
              id="dataSiteContact1"
              name="dataSiteContact1"
              value={values.dataSiteContact1}
            />
            {errors.dataSiteContact1 && touched.dataSiteContact1 ? (
              <p className="error">{errors.dataSiteContact1}</p>
            ) : null}
          </InputBlock>
        </div>
        <div>
          <InputBlock
            useSmall
            hasError={!!errors.dataSiteContact2 && !!touched.dataSiteContact2}
            title="Secondary Data Site">
            <EmailInput
              domain={values.emailDomain.split(',')[0]}
              onChange={handleChange}
              onBlur={handleBlur}
              type="text"
              id="dataSiteContact2"
              name="dataSiteContact2"
              value={values.dataSiteContact2}
              disabled={Bixby.Utils.isNullOrEmpty(values.dataSiteContact1)}
            />
            {errors.dataSiteContact2 && touched.dataSiteContact2 ? (
              <p className="error">{errors.dataSiteContact2}</p>
            ) : null}
          </InputBlock>
        </div>
        {isBixbyComplianceEnabled && values.hasBixbyCompliance && isBixbyComplianceAdmin && (
          <>
            <div>
              <InputBlock
                useSmall
                hasError={!!errors.primaryComplianceAdmin && !!touched.primaryComplianceAdmin}
                title="Primary Compliance Admin"
                required>
                <EmailSelectInput
                  datasource={
                    users?.map(_ => {
                      return { id: _.email };
                    }) ?? []
                  }
                  onChange={v => {
                    const value = v.currentTarget.value;
                    setFieldValue('primaryComplianceAdmin', value);
                    if (Bixby.Utils.isNullOrEmpty(value)) {
                      setFieldValue('secondaryComplianceAdmin', value);
                    }
                  }}
                  id="primaryComplianceAdmin"
                  value={values.primaryComplianceAdmin}
                />
                {values.hasBixbyCompliance && errors.primaryComplianceAdmin ? (
                  <p className="error">{errors.primaryComplianceAdmin}</p>
                ) : null}
              </InputBlock>
            </div>
          </>
        )}
      </div>
      <p className="error">{status}</p>
      {complianceDisableReason ? <p className="error">{complianceDisableReason}</p> : null}
      {isSubmitting && <div>Loading...</div>}
      <BixButton type="submit" disabled={isSubmitting || !isValid} onClick={handleSubmit}>
        {submitButtonText}
      </BixButton>
    </form>
  );
};

const mapDispatchToProps = () => {
  return {};
};

const mapStateToProps = (state: AppState) => {
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isSFDREnabled } = getSFDREnabled(state);
  const { isLegalAdmin } = getIsCurrentUserLegalAdminState(state);

  return {
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
    isSFDREnabled,
    isLegalAdmin,
    isSuperAdmin,
    users: (state.clientState.currentClient || ({} as Bixby.Models.Client)).contacts || [],
    subscriptions: state.subscriptionState.data
      ? (Bixby.Utils.orderBy(state.subscriptionState.data, i => i.name) as Bixby.Models.IIdentifiable[])
      : [],
  };
};

const UpdateClientForm = withFormik<IClientFormikProps, UpdateClientFormValues>({
  mapPropsToValues: (props: IClientFormikProps) => {
    const valueOrEmpty = (arr?: Bixby.Models.IIdentifiable[]) =>
      arr && arr.length > 0 ? Bixby.Utils.first(arr).id : '';

    return props.initialValues
      ? props.initialValues
      : {
          clientName: '',
          clientEmail: '',
          dataSiteContact1: '',
          dataSiteContact2: '',
          dataSiteEmail: '',
          mnpiDataSiteEmail: '',
          emailDomain: '',
          clientNotes: '',
          primaryContact1: '',
          primaryContact2: '',
          subscriptionTier: valueOrEmpty(props.subscriptions),
          hasAnalytics: false,
          hasExcelAccess: false,
          hasCompanyTranscriptions: false,
          hasCompanyDealNotes: false,
          hasCompanyDocuments: false,
          hasCloAccess: false,
          hasCreditSightsCovenantReview: false,
          hasPreapprovedCompanyAccess: false,
          hasBixbyCompliance: false,
          hasBixbyComplianceAutoApproval: false,
          hasBixbyComplianceDemoMode: false,
          isPrivateOnAllNames: false,
          canReachOutToBankOnBehalf: false,
          usersCannotLogin: false,
          hasSFDR: false,
          hasUsEuroGrouping: false,
          isSufPermissioned: false,
          isRatingsPermissioned: false,
          isOnFitchPaper: false,
          isCovenantCrossPermissioned: false,
          isInPreview: false,
          isCutoverToCreditSights: false,
          allowDocumentSharing: true,
          hasClientProvidedDataSites: false,
          hasCloPermissions: false,
          includeWithDataAggregation: false,
          hasPortfolioManagerWorkflow: false,
        };
  },

  validationSchema: Yup.object().shape({
    dataSiteContact1: Yup.string(),
    dataSiteContact2: Yup.string(),
    primaryContact1: Yup.string().when(['usersCannotLogin', 'isCutoverToCreditSights'], {
      is: (usersCannotLogin: boolean, isCutoverToCreditSights: boolean) =>
        !usersCannotLogin && !isCutoverToCreditSights,
      then: schema => schema.required('Primary Contact is required.'),
      otherwise: schema => schema,
    }),
    primaryContact2: Yup.string(),
    subscriptionTier: Yup.string().required('Subscription Tier is required'),
    hasAnalytics: Yup.boolean(),
    hasExcelAccess: Yup.boolean(),
    hasCompanyTranscriptions: Yup.boolean(),
    hasCompanyDealNotes: Yup.boolean(),
    hasCompanyDocuments: Yup.boolean(),
    hasCloAccess: Yup.boolean(),
    hasCreditSightsCovenantReview: Yup.boolean(),
    hasPreapprovedCompanyAccess: Yup.boolean(),
    hasBixbyCompliance: Yup.boolean(),
    hasBixbyComplianceAutoApproval: Yup.boolean(),
    hasBixbyComplianceDemoMode: Yup.boolean(),
    isPrivateOnAllNames: Yup.boolean(),
    canReachOutToBankOnBehalf: Yup.boolean(),
    hasSDFR: Yup.boolean(),
    hasUsEuroGrouping: Yup.boolean(),
    isSufPermissioned: Yup.boolean(),
    isRatingsPermissioned: Yup.boolean(),
    isOnFitchPaper: Yup.boolean(),
    isCovenantCrossPermissioned: Yup.boolean(),
    includeWithDataAggregation: Yup.boolean(),
    hasPortfolioManagerWorkflow: Yup.boolean(),
    allowDocumentSharing: Yup.boolean(),
    hasClientProvidedDataSites: Yup.boolean(),
    hasCloPermissions: Yup.boolean(),
    primaryComplianceAdmin: Yup.string().when('hasBixbyCompliance', {
      is: (hasBixbyCompliance: boolean) => hasBixbyCompliance,
      then: schema =>
        schema.required('Primary Compliance Admin is required.').min(1, 'Primary Compliance Admin is required.'),
      otherwise: schema => schema,
    }),
    mnpiDataSiteEmail: Yup.string().when('hasBixbyCompliance', {
      is: (hasBixbyCompliance: boolean) => hasBixbyCompliance,
      then: schema => schema.required('MNPI Data Site Email is required.').min(1, 'MNPI Data Site Email is required.'),
      otherwise: schema => schema,
    }),
    isInPreview: Yup.boolean(),
  }),

  // initialValues property dspPasswords is updated once allDataSiteProviders is returned in ClientInfo component
  enableReinitialize: true,

  handleSubmit: async (values: UpdateClientFormValues, { props, setSubmitting, setStatus }) => {
    setStatus(undefined);

    try {
      setSubmitting(true);
      const updates = { ...values };
      await props.onSubmit?.(updates);
    } catch (e: any) {
      const message: string = Bixby.API.getErrorMessage(e);
      setStatus(message);
    }

    setSubmitting(false);
  },
})(UpdateClientFormik);

export default connect(mapStateToProps, mapDispatchToProps)(UpdateClientForm);
